// Единый источник «дерева вкладок отдела» — навигация (сайдбар), страница отдела
// и матрица прав (user-management) берут его ОТСЮДА. Копий больше нет: до
// 26.08.2026 departments-workspace строил своё дерево параллельно, и это
// требовало держать два файла в синхроне вручную.

// requires — синтет-вкладка отдела появляется только если в отделе есть этот модуль (для Sales-вкладок).
export type TabNode = {
  key: string;
  label: string;
  children?: TabNode[];
  requires?: string;
  /** Короткое описание раздела под заголовком экрана. Говорит, ЗАЧЕМ раздел, и
   *  НЕ перечисляет свои же подразделы: пересказ вкладок словами — шум
   *  (правило Максима, 27.08.2026). У разделов-модулей
   *  подставляется описание модуля с бэкенда, у синтетических — берётся отсюда.
   *  Заголовок и описание раздела рисует страница отдела, поэтому и текст должен
   *  жить рядом с названием, а не внутри модуля (Максим, 27.08.2026). */
  hint?: string;
};

// Псевдо-ключи верхнеуровневых вкладок (совпадают с departments-workspace).
export const DASHBOARD_KEY = "__dashboard";
export const REPORT_KEY = "__report";
// «Аналитик» — разбор для собственника: что происходит, куда придём, что делать.
// Пока обкатывается, живёт в «Настройки → Продажи · WB» с плашкой «в доработке»
// (SALES_SETTINGS_SECTIONS, ключ brief) — тем же путём, что ОПиУ и Реклама.
// Ключ оставлен: когда экран вернётся вкладкой в отдел, менять придётся только
// здесь и в departments-workspace.
export const BRIEF_KEY = "__brief";
export const STATISTICS_KEY = "__statistics";
export const FINANCE_KEY = "__finance";
export const PRODUCTS_KEY = "__products";
export const WAREHOUSE_KEY = "__warehouse";
export const X10_KEY = "__x10";
export const MPPULSE_KEY = "__mppulse";
// Маркетинг: верхнеуровневые вкладки рядом с Дашбордом, все рендерят creator_reports:
// «Креаторы» — инструмент публикаций (подвкладка reports);
// «Коммерция»/«Бартеры» — каналы из MarketingTab (mkt_reports), вынесены из вкладки «Маркетинг».
export const CREATORS_KEY = "__creators";
export const COMMERCE_KEY = "__commerce";
export const BARTERS_KEY = "__barters";
export const BLOGGERS_KEY = "__bloggers";
export const BARTERERS_KEY = "__barterers";
export const INTEGRATIONS_KEY = "__integrations";
export const SOCIAL_KEY = "__social";  // «Наши соцсети» (бывш. подвкладка «Маркетинг»)

export const TABBED_DEPARTMENTS = new Set(["rd", "sales", "marketing", "ai", "hr"]);
// HR: свой «Дашборд» — одна из 6 вкладок, отдельная синтет-вкладка отдела не нужна.
export const TABBED_DEPTS_NO_DASHBOARD = new Set(["ai", "hr"]);
export const hasDeptDashboard = (deptKey: string) =>
  TABBED_DEPARTMENTS.has(deptKey) && !TABBED_DEPTS_NO_DASHBOARD.has(deptKey);

/** Ключ псевдо-отдела «Команда» в матрице прав: раздел верхнего уровня, но
 *  права ему нужны такие же, как отделу (Максим, 04.09.2026: «не каждый раздел
 *  должен быть доступен каждому сотруднику»). До этого «Команда» и все десять
 *  вкладок карточки были открыты любому, у кого есть вход в портал. */
export const TEAM_DEPT_KEY = "team";
export const TEAM_ROSTER_KEY = "__roster";
export const TEAM_PROFILE_KEY = "__profile";

/** Вкладки карточки сотрудника — порядок и подписи те же, что в самой карточке
 *  (components/team/employee-profile.tsx). Ключи совпадают со значениями вкладок:
 *  так право и вкладка связаны напрямую, без таблицы соответствий. */
export const TEAM_PROFILE_TABS: TabNode[] = [
  { key: "about", label: "О сотруднике", hint: "Должность, отдел, контакты, руководитель" },
  { key: "tasks", label: "Задачи", hint: "Задачи сотрудника и ход работы по ним" },
  { key: "calendar", label: "Календарь", hint: "Встречи, отпуска и события сотрудника" },
  { key: "docs", label: "Документы", hint: "Личное дело и рабочие бумаги" },
  { key: "okr", label: "Цели и OKR", hint: "Цели сотрудника и результаты по ним" },
  { key: "review", label: "Оценка", hint: "Оценки и обратная связь по сотруднику" },
  { key: "money", label: "Зарплата", hint: "Оклад, выплаты и история изменений" },
  { key: "access", label: "Доступы и роли", hint: "Роль в портале, права и журнал выдачи" },
  { key: "activity", label: "Активность", hint: "Где сотрудник бывает в портале и когда" },
  { key: "settings", label: "Настройки", hint: "Служебные настройки карточки" },
];

/** Дерево «Команды» для матрицы прав и для проверок доступа. */
export const TEAM_TABS: TabNode[] = [
  { key: TEAM_ROSTER_KEY, label: "Список и оргструктура", hint: "Кто работает в компании и как устроены отделы" },
  { key: TEAM_PROFILE_KEY, label: "Карточка сотрудника", hint: "Открывать карточку коллеги", children: TEAM_PROFILE_TABS },
];

// Подвкладки модулей (данные; иконки — в departments-workspace).
export const MODULE_SUBTABS: Record<string, TabNode[]> = {
  // Было семь вкладок. «Онбординг» и «Офбординг» схлопнуты в «Оформление»
  // (вход и выход человека — один процесс), «Сотрудники» убраны: работа с
  // человеком идёт в его карточке из оргструктуры, уволенные — за кнопкой
  // «Архив» там же. Старые ключи прав (tab:hr:onboarding / offboarding / staff)
  // ни на что больше не влияют — доступ выдаётся заново на tab:hr:process.
  hr: [
    { key: "dashboard", label: "Дашборд", hint: "Ключевые показатели по персоналу: штат, ФОТ, отпуска" },
    // «Оргструктура» переехала в раздел «Команда» (пункт меню верхнего уровня):
    // структура компании — это про людей всей компании, а не про внутреннюю
    // кухню отдела HR (Максим, 27.08.2026).
    { key: "hiring", label: "Найм", hint: "Кандидаты по этапам отбора, вакансии и оценка резюме" },
    { key: "process", label: "Оформление", hint: "Вход и выход человека: чек-листы приёма и увольнения" },
    { key: "vacations", label: "Отпуска", hint: "График отпусков и остаток дней по сотрудникам" },
  ],
  labels: [
    { key: "print", label: "Печать" },
    { key: "products", label: "Товары" },
    { key: "templates", label: "Шаблоны" },
  ],
  purchases: [
    { key: "orders", label: "Заказы" },
    { key: "pivot", label: "В производстве / в пути" },
    { key: "logistics", label: "Логистика рейсов" },
    { key: "msklad", label: "Приёмки" },
    { key: "costprice", label: "Себестоимость" },
    { key: "refs", label: "Справочники" },
  ],
  assortment: [
    { key: "assortment", label: "Ассортимент" },
    { key: "search", label: "Поиск новинок" },
    { key: "funnel", label: "Воронка" },
    { key: "final", label: "Просчёт заказа" },
  ],
  planning: [
    { key: "import", label: "Импорт MPSTATS" },
    { key: "pnl", label: "Планирование по SKU" },
    { key: "summary", label: "Планирование по месяцам" },
  ],
  // creator_reports: вкладка-модуль «Отчёты креаторов» скрыта в Маркетинге (04.07) —
  // всё разнесено по верхним вкладкам: «Отчёты»→Креаторы, «Артикулы» убраны,
  // «Подменные» — кнопки в тулбаре Креаторов и в PartnerTab Блогеров/Бартеров,
  // «Настройки» — шестерёнка (только platform_admin), «Выплаты» — кнопка в тулбаре.
  wb_analytics: [
    // Ключ группы остался "weekly" — по нему выданы права; сама вкладка
    // «По неделям» снята 07.08.2026 вместе со «Сравнением».
    { key: "weekly", label: "Статистика", children: [
      // «РНП (10X)» снят 13.08.2026: остался только «Рука на пульсе (mppulse)» —
      // те же заказы/воронка/реклама, но с дневным P&L. План заказов и ДРР по
      // товару, ради которого экран жил, за всё время не завели ни разу —
      // таблица плана и её эндпоинт снесены 18.08.2026 как мёртвый груз.
      { key: "consolidated", label: "Сводный чек-лист" },
      { key: "checklist", label: "Чек-лист" },
    ] },
    { key: "unit", label: "Финансы", children: [
      { key: "unit", label: "Юнит-экономика" },
      { key: "sku-pl", label: "P&L по SKU" },
    ] },
    // Ключ группы остался "stocks" (по нему выданы права), хотя самой вкладки
    // «Остатки» больше нет — её оборачиваемость переехала в «Остатки по складам».
    { key: "stocks", label: "Склад & Логистика", children: [
      { key: "funnel", label: "Воронка" },
    ] },
  ],
};

// Разделы «Продаж», скрытые от ВСЕХ: не доделаны, показывать никому не нужно
// (решение Олега 29.07.2026). Это НЕ права по вкладкам: там видимость настраивается
// на пользователя, а здесь вкладка не попадает ни в навигацию, ни в матрицу прав.
// Вернуть раздел — убрать его ключ отсюда; сами вкладки с подвкладками остались ниже.
export const HIDDEN_SALES_TABS = new Set<string>([MPPULSE_KEY, X10_KEY, FINANCE_KEY, PRODUCTS_KEY]);

// То же самое, но для ПОДВКЛАДОК внутри видимой вкладки. «Склад & Логистика»: в работе
// только «Приход товара» и «Ассортиментная матрица», остальное недоделано (29.07.2026).
export const HIDDEN_SALES_SUBTABS: Record<string, Set<string>> = {
  // «Возвраты» и «Поставки» удалены 07.08.2026 — ключи из набора убраны, чтобы
  // список скрытого не оброс мёртвыми именами. «Остатки» уедут следом: их
  // оборачиваемость переезжает в «Остатки по складам».
  [WAREHOUSE_KEY]: new Set(["funnel"]),
};
export const isHiddenSalesSub = (topKey: string, subKey: string): boolean =>
  !!HIDDEN_SALES_SUBTABS[topKey]?.has(subKey);

// Синтетические верхнеуровневые вкладки отдела «Продажи» (агрегируют табы разных модулей).
// requires — модуль, при наличии которого вкладка появляется (единый источник условия для нав + прав).
const SALES_SYNTH_ALL: TabNode[] = [
  // «Отчёт» — сводный ГПР по кабинетам (Feevi/Enmoone) + Общий: заказы/выкупы/
  // % выкупа/маржа за день/неделю/период. Кастомный компонент SalesReportTab.
  { key: REPORT_KEY, label: "Отчёт", requires: "sales", hint: "Дневной ГПР по каждому кабинету: план, факт, светофор и разрыв" },
  // «mppulse» — отдельная верхнеуровневая вкладка-зеркало дашборда mppulse.
  // Внутри — навигация по РАЗДЕЛАМ (как боковые группы сервиса mppulse:
  // Оцифровка/Реклама/Инструменты). Пока единственный раздел — «Оцифровка»
  // (текущий дашборд). Структура расширяема: добавляйте children (раздел →
  // ключ суб-таба SalesWorkspace или своего компонента) — рендер в
  // departments-workspace по resolvedActive === MPPULSE_KEY.
  // Группа называлась «mppulse» — по имени стороннего сервиса, чью методику
  // повторяют эти экраны. Пользователю портала это ничего не говорит.
  { key: MPPULSE_KEY, label: "Аналитика", requires: "sales", hint: "Оцифровка продаж и отдача рекламы по кабинетам", children: [
    { key: "digitization", label: "Оцифровка" },
    { key: "rnp", label: "РНП" },
    { key: "opiu", label: "ОПиУ" },
    { key: "advertising", label: "Реклама" },
  ] },
  // «10X» — все вкладки портала, сделанные по эталону 10X (созвон 10.06: перенести
  // в отдельную выпадашку).
  { key: X10_KEY, label: "Сверка и чек-листы", requires: "sales", hint: "Сходятся ли цифры кабинета с нашими и что мешает выполнить план", children: [
    { key: "checklist", label: "Чек-лист" },
    { key: "consolidated", label: "Сводный чек-лист" },
    { key: "unit", label: "Юнит-экономика" },
    { key: "sku-pl", label: "Фин. отчёт по SKU" },
  ] },
  { key: FINANCE_KEY, label: "Финансы", requires: "sales", hint: "Сколько заработали после всех расходов", children: [
  ] },
  { key: WAREHOUSE_KEY, label: "Склад & Логистика", requires: "sales", hint: "Движение товара: что в пути, что на складах и чего не хватает", children: [
    // «Приход товара» — что едет из R&D и когда придёт (Ali: «в складе я бы сделал приход
    // товара»). Данные из rd_collection, поэтому подвкладка рендерится своим компонентом,
    // а не SalesWorkspace (см. departments-workspace.tsx, ветка WAREHOUSE_KEY).
    { key: "incoming", label: "Приход товара" },
    // «Ассортиментная матрица» — база всех артикулов сезона + паспорт (ТН ВЭД/состав).
    // Данные из rd_collection → рендерится своим компонентом (см. departments-workspace).
    { key: "matrix", label: "Ассортиментная матрица" },
    // «Остатки по складам» — суточная история остатков в разрезе складов WB со своим
    // компонентом (WarehouseStockTab): нужен выбор складов, потому что сгоревшие
    // склады WB продолжают отдавать остатки и завышают запас в расчётах.
    { key: "wh-stock", label: "Остатки по складам" }, { key: "funnel", label: "Воронка" },
  ] },
];

// Подписи ВСЕХ синтет-вкладок, включая скрытые и убранные из дерева отдела. Нужны
// там, где сохранённый ключ надо показать человеку задним числом (напр. виджет
// «Перевод отделов на портал» хранит ключи вкладок): без этой карты подпись
// вырождалась в сырой ключ вроде «__x10».
export const SYNTH_TAB_LABELS: Record<string, string> = Object.fromEntries([
  ...SALES_SYNTH_ALL.map((n) => [n.key, n.label] as const),
  [DASHBOARD_KEY, "Дашборд"],
  [INTEGRATIONS_KEY, "Интеграции"],
  [CREATORS_KEY, "Креаторы"],
  [BLOGGERS_KEY, "Блогеры"],
  [BARTERERS_KEY, "Бартеры"],
  [COMMERCE_KEY, "Коммерция"],
  [BARTERS_KEY, "Бартеры"],
  [SOCIAL_KEY, "Наши соцсети"],
]);

// То, что реально видят навигация (departments-workspace) и матрица прав: без скрытых
// разделов и без скрытых подвкладок внутри оставшихся.
export const SALES_SYNTH: TabNode[] = SALES_SYNTH_ALL
  .filter((n) => !HIDDEN_SALES_TABS.has(n.key))
  .map((n) => {
    const hidden = HIDDEN_SALES_SUBTABS[n.key];
    return hidden && n.children ? { ...n, children: n.children.filter((c) => !hidden.has(c.key)) } : n;
  });

// Порядок верхних вкладок «Продаж» слева-направо — решение Ali (созвон 15.07):
// «слева всё актуальное, справа хвостик неактуального… здесь я бы оставил Отчёт,
// Планирование, Склад». Ключи НЕ из списка уезжают в хвост, сохраняя свой относительный
// порядок, — так новая вкладка не потеряется молча, а просто окажется справа.
//
// ⚠️ Скрытие вкладок ОТ КОНКРЕТНЫХ ЛЮДЕЙ делается НЕ здесь: это права по вкладкам
// (`tab:<dept>:<key>`, «Управление пользователями»). Ali снимает их сам («я проскрою все»).
// Порядок и видимость — разные вещи; смешивать их в коде не надо.
// Исключение — недоделанные разделы, которые не должен видеть НИКТО: они перечислены
// в HIDDEN_SALES_TABS выше и вырезаются из дерева на уровне кода.
export const SALES_TAB_ORDER: string[] = [
  DASHBOARD_KEY,   // «Дашборд — это месяц, где мы по плану» (Ali)
  REPORT_KEY,      // дневной ГПР — то, что смотрят каждый день и шлют в чат
  "planning",      // Планирование YUC — сезон/распродажа/P&L
  WAREHOUSE_KEY,   // Склад & Логистика — приходы, распределение
];

// Стабильная сортировка по SALES_TAB_ORDER: известные ключи — слева в заданном порядке,
// остальные — в хвост, не меняя своего исходного порядка между собой.
export function sortSalesTabs<T extends { key: string }>(items: T[]): T[] {
  const rank = (k: string) => {
    const i = SALES_TAB_ORDER.indexOf(k);
    return i === -1 ? SALES_TAB_ORDER.length : i;
  };
  return items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => rank(a.it.key) - rank(b.it.key) || a.i - b.i)
    .map((x) => x.it);
}

// Полное дерево вкладок отдела (для матрицы прав). deptModules — модули, назначенные отделу.
// Модули, скрытые из меню ОТДЕЛА, но живые: вход к ним — из «Настроек»
// (Максим, 28.08.2026: «эти две вкладки нужно спрятать в настройках, как мы
// сделали с отделом продаж»). Приём тот же, что у HIDDEN_SALES_TABS: вкладка не
// попадает ни в навигацию, ни в матрицу прав по вкладкам, а сам модуль и его
// право остаются. Вернуть в меню — убрать ключ отсюда.
export const HIDDEN_DEPT_MODULES = new Set<string>([
  "images",     // «Генерация картинок» — прототип на Codex CLI
  "workflows",  // «Сценарии»
]);

export function buildDeptTabTree(
  deptKey: string,
  deptModules: { key: string; title: string; description?: string }[],
): TabNode[] {
  const has = (k: string) => deptModules.some((m) => m.key === k);
  const tree: TabNode[] = [];
  // «Команда» — раздел верхнего уровня, модулей у него нет, дерево своё.
  if (deptKey === TEAM_DEPT_KEY) return TEAM_TABS;
  if (hasDeptDashboard(deptKey)) tree.push({ key: DASHBOARD_KEY, label: "Дашборд", hint: "Ключевые показатели отдела за выбранный период" });
  // HR: 6 синтет-вкладок отдела (весь модуль `hr` живёт в них, сам модуль-таб скрыт).
  if (deptKey === "hr") {
    // hint переносим тоже — иначе описание раздела теряется по дороге.
    for (const t of MODULE_SUBTABS.hr ?? []) tree.push({ key: t.key, label: t.label, hint: t.hint });
    return tree;
  }
  if (deptKey === "marketing" && has("creators")) {
    // Порядок — созвон 03.07.2026: Интеграции (руководитель) → исполнительские вкладки.
    // «Наши соцсети» (SOCIAL_KEY) скрыта: соцсети не ведём до нового продукта.
    tree.push({ key: INTEGRATIONS_KEY, label: "Интеграции", hint: "Размещения у блогеров: план, факт и отдача" });
    tree.push({ key: CREATORS_KEY, label: "Креаторы", hint: "Публикации креаторов и охваты по площадкам" });
    tree.push({ key: BLOGGERS_KEY, label: "Блогеры", hint: "База блогеров: условия, контакты и история работы" });
    tree.push({ key: BARTERERS_KEY, label: "Бартеры", hint: "Бартерные размещения и что они принесли" });
  }
  if (deptKey === "sales") {
    for (const node of SALES_SYNTH) if (!node.requires || has(node.requires)) tree.push(node);
  }
  for (const m of deptModules) {
    if (deptKey === "sales" && m.key === "sales") continue; // в продажах скрыт (его табы — синтетические)
    if (deptKey === "marketing" && m.key === "creators") continue; // скрыт: всё в верхних вкладках (04.07)
    if (HIDDEN_DEPT_MODULES.has(m.key)) continue;                  // спрятан в «Настройки» (28.08)
    // Описание раздела-модуля берём с бэкенда — второй копии текста не заводим.
    tree.push({ key: m.key, label: m.title, hint: m.description, children: MODULE_SUBTABS[m.key] });
  }
  // Продажи: актуальное — слева, остальное — в хвост (решение Ali, созвон 15.07).
  // Модули иначе всегда падали бы в конец, а «Планирование YUC» должно стоять рядом с Отчётом.
  return deptKey === "sales" ? sortSalesTabs(tree) : tree;
}

// Стабильный ключ права на вкладку. Хранится в ModulePermission.moduleKey.
export function tabPermKey(deptKey: string, topKey: string, childKey?: string): string {
  return childKey ? `tab:${deptKey}:${topKey}:${childKey}` : `tab:${deptKey}:${topKey}`;
}

// Маппинг верхней вкладки → модули (для деривации module-level прав: бэкенд гейтит по модулю).
export function tabTopToModules(deptKey: string, topKey: string): string[] {
  if (topKey === DASHBOARD_KEY) return [];
  if (deptKey === "hr") return ["hr"]; // все синтет-вкладки HR гейтятся модулем `hr`
  if (deptKey === "marketing" && (topKey === CREATORS_KEY || topKey === COMMERCE_KEY || topKey === BARTERS_KEY || topKey === BLOGGERS_KEY || topKey === BARTERERS_KEY || topKey === INTEGRATIONS_KEY || topKey === SOCIAL_KEY)) return ["creators"];
  if (deptKey === "sales") {
    if (topKey === REPORT_KEY) return ["sales"];
    if (topKey === MPPULSE_KEY) return ["sales"];
    if (topKey === X10_KEY) return ["sales"];
    if (topKey === FINANCE_KEY) return ["sales"];
    if (topKey === PRODUCTS_KEY) return ["sales"];
    if (topKey === WAREHOUSE_KEY) return ["sales"];
  }
  return [topKey]; // обычная вкладка-модуль: top == ключ модуля
}

// Все ключи прав отдела (верхние + дочерние) — для тумблера «доступ к отделу» и счётчиков.
export function allTabPermKeys(deptKey: string, deptModules: { key: string; title: string }[]): string[] {
  const keys: string[] = [];
  for (const t of buildDeptTabTree(deptKey, deptModules)) {
    keys.push(tabPermKey(deptKey, t.key));
    for (const c of t.children ?? []) keys.push(tabPermKey(deptKey, t.key, c.key));
  }
  return keys;
}
